import React, { useEffect, useState } from "react";
import {FormControl, InputLabel, MenuItem, Select, CircularProgress} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { fetchAvailableFirmware, fetchAvailableDevFirmware, fetchAvailableAdminFirmware } from "../../../redux/actions/firmware.actions";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { AppDispatch } from "../../../redux/store";
import { palName } from "../../../utils/brand";
import ModalShell, { ModalSpaList } from "./ModalShell";
import { isV1Spa } from "../../../hooks/useSpaCommand";
import { isV1Yoctub } from "../../../pages/SpaReport/options";
import { fetchV1FirmwareApi, V1Firmware, V1FirmwareChannel } from "../../../services/api/v1Firmware.api";

interface ModalProps {
  isOpen: boolean,
  setOpen: any,
  setOpenSingle?: any,
  setOpenMultiple?: any,
  setFirmware: any,
  setVersion: any,
  /** V1 spas only: which legacy catalog the version came from (the backend re-checks it). */
  setChannel?: (channel: V1FirmwareChannel) => void,
  spas: any[],
}

const ConfirmFirmwareModal: React.FC<ModalProps> = ({
  isOpen,
  setOpen,
  setOpenSingle=null,
  setOpenMultiple=null,
  setFirmware,
  setVersion,
  setChannel,
  spas
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {data: availableFirmware, loading} = useSelector((state: RootState) => state.firmware);
  const [selectedFirmware, setSelectedFirmware] = useState<any>("");
  const [selectedVersion, setSelectedVersion] = useState<any>("");
  const { canAccess: canAccessDev } = usePermissionCheck(['view_dev_firmware']);
  const { canAccess: canAccessAdmin } = usePermissionCheck(['view_admin_firmware']);
  // Admin firmware is a subset of dev firmware, so the Admin tab is redundant for
  // users who can already see the Dev tab.
  const showAdminTab = canAccessAdmin && !canAccessDev;
  const [activeTab, setActiveTab] = useState(0);
  const accessToken = useSelector((state: RootState) => state.user.accessToken);

  // V1 / Coldfire spas update from the legacy catalog over the legacy SpaServer; V2 and V1
  // version numbers must never cross, so a selection has to be all one generation.
  const v1Count = spas.filter((spa: any) => isV1Spa(spa)).length;
  const isV1 = spas.length > 0 && v1Count === spas.length;
  const isMixed = v1Count > 0 && !isV1;
  const [v1Firmware, setV1Firmware] = useState<V1Firmware[]>([]);
  const [v1Loading, setV1Loading] = useState(false);
  const [v1Error, setV1Error] = useState<string | null>(null);

  // Components that fit the packs. The spa list does not always carry a product code, so
  // when any is unknown offer all four; the backend skips spas a component does not fit.
  const v1Platforms = (() => {
    const known = spas.every((spa: any) => spa?.product_code != null);
    const yoctub = spas.some((spa: any) => isV1Yoctub(spa));
    const coldfire = spas.some((spa: any) => !isV1Yoctub(spa));
    return [
      ...(!known || yoctub ? [['yocto', 'Yocto'], ['lpc', 'LPC'], ['spaboy', palName]] : []),
      ...(!known || coldfire ? [['cf', 'Coldfire']] : []),
    ];
  })();

  useEffect(() => {
    if (!isOpen || !isV1) return;
    let cancelled = false;
    setV1Loading(true);
    setV1Error(null);
    fetchV1FirmwareApi(accessToken, activeTab === 1 && canAccessDev ? 'dev' : 'public')
      .then((list) => { if (!cancelled) setV1Firmware(list); })
      .catch((e) => { if (!cancelled) { setV1Firmware([]); setV1Error(e?.response?.data?.message || 'Could not load the V1 firmware list'); } })
      .finally(() => { if (!cancelled) setV1Loading(false); });
    return () => { cancelled = true; };
  }, [isOpen, isV1, activeTab, canAccessDev, accessToken]);

  useEffect(() => {
    if (isV1 || isMixed) return;
    if (activeTab === 0) {
      dispatch(fetchAvailableFirmware(""));
    } else if (canAccessDev) {
      dispatch(fetchAvailableDevFirmware(""));
    } else if (showAdminTab) {
      dispatch(fetchAvailableAdminFirmware(""));
    }
}, [dispatch, activeTab, canAccessDev, showAdminTab, isV1, isMixed]);


  const handleReset = () => {
    setSelectedFirmware("");
    setSelectedVersion("");
    setActiveTab(0);
  };


  const handleClose = () => {
    handleReset();
    setOpen(false);
  };

  const handleTabChange = (newValue: number) => {
    setSelectedVersion("");
    setActiveTab(newValue);
  };

  const handleConfirm = () => {
    setFirmware(selectedFirmware);
    setVersion(selectedVersion);
    setChannel?.(activeTab === 1 && canAccessDev ? 'dev' : 'public');
    if (setOpenSingle) {
      setOpenSingle(true);
    } else if (setOpenMultiple) {
      setOpenMultiple(true);
    }
    handleClose();
  }

  const handleFirmware = (e:any) => {
    let val = e.target.value;
    setSelectedFirmware(val)
    if (val==='all') {
      setSelectedVersion('latest')
    } else {
      setSelectedVersion('')
    }
  }

  const catalog: any[] = isV1 ? v1Firmware : availableFirmware;
  const catalogLoading = isV1 ? v1Loading : loading;
  const versions = catalog.filter((f:any)=>f.type===selectedFirmware);
  const noVersions = selectedFirmware!=="" && selectedFirmware!=="all" && versions.length===0 && !catalogLoading;

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      title={spas.length>1 ? `Update firmware on ${spas.length} spas` : "Update firmware"}
      subtitle="Pick which firmware to install and the version to install."
      actions={
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={isMixed || spas.length===0 || selectedFirmware==="" || selectedVersion===""}
            onClick={handleConfirm}
          >
            Update firmware
          </button>
        </>
      }
    >
      {isMixed && <div className="dlg-notice warning">
        This selection mixes V1 and V2 spas. They use different firmware, so update them separately.
      </div>}
      {isV1 && <div className="dlg-notice">
        V1 spa: versions come from the classic portal's firmware list and are sent through the legacy spa server.
      </div>}
      {v1Error && <div className="dlg-notice error">{v1Error}</div>}

      {(isV1 ? canAccessDev : (canAccessDev || showAdminTab)) && (
        <div className="segmented" role="tablist" aria-label="Firmware channel" style={{alignSelf: 'flex-start'}}>
          <button type="button" role="tab" aria-selected={activeTab===0} className={activeTab===0 ? 'active' : ''} onClick={() => handleTabChange(0)}>Public</button>
          <button type="button" role="tab" aria-selected={activeTab===1} className={activeTab===1 ? 'active' : ''} onClick={() => handleTabChange(1)}>
            {canAccessDev ? 'Dev' : 'Admin'}
          </button>
        </div>
      )}

      <ModalSpaList spas={spas} label={spas.length===1 ? 'Spa to update' : 'Spas to update'}/>

      <div className="dlg-row">
        <FormControl fullWidth size="small">
          <InputLabel id="firmware-selection-label">Firmware</InputLabel>
          <Select
            labelId="firmware-selection-label"
            label="Firmware"
            onChange={(e:any) =>handleFirmware(e)}
            value={selectedFirmware}
          >
            {isV1 ? v1Platforms.map(([value, label]) =>
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ) : [
              <MenuItem key="all" value={`all`}>All</MenuItem>,
              <MenuItem key="yocto" value={`yocto`}>Yocto</MenuItem>,
              <MenuItem key="lpc" value={`lpc`}>LPC</MenuItem>,
              <MenuItem key="spaboy" value={`spaboy`}>{palName}</MenuItem>,
            ]}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="version-selection-label" disabled={catalog.length===0 || catalogLoading}>Version</InputLabel>
          <Select
            labelId="version-selection-label"
            label="Version"
            onChange={e => setSelectedVersion(e.target.value)}
            disabled={selectedFirmware==="" || catalog.length===0 || catalogLoading}
            value={selectedVersion??""}
            endAdornment={catalogLoading ? <CircularProgress size={16} sx={{mr: 3}}/> : undefined}
          >
            {selectedFirmware!=='all' ?
              versions.map((f:any,i:number)=>
                <MenuItem key={i} value={f?.version}>{f?.version}{isV1 && f?.is_latest ? " (latest)" : ""}</MenuItem>
              )
              :
              <MenuItem value={'latest'}>{'Latest'}</MenuItem>
            }
          </Select>
        </FormControl>
      </div>

      {noVersions && <div className="dlg-notice warning">No firmware found for this type.</div>}
    </ModalShell>
  );
};

export default ConfirmFirmwareModal;
